import { ConfirmationResult } from "firebase/auth";
import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router";
import styled from "styled-components";
import googleIcon from "@assets/images/auth/google.svg";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { RegisterError } from "@domain/auth/authError";
import { messageForFirebaseError } from "@domain/auth/firebaseErrorMessage";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { useAsync } from "@utils/hooks";
import { isEmail } from "@utils/regex";
import { Button } from "@ui/components/buttons/button";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { checkboxInputCss } from "@ui/components/inputs/checkboxInput";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { desktop, gap } from "@ui/styles/utils";
import { usePrivacyConfirmation } from "@ui/pages/loginPage/privacy/usePrivacyConfirmation";
import { SignupChannel, useSignupStepCount } from "./authStep";

interface SignupProps {
	step: number;
	channel: SignupChannel;
	onStepChange: (step: number) => void;
	onChannelChange: (channel: SignupChannel) => void;
	onLogin: () => void;
}

export const Signup: React.FC<SignupProps> = ({ step, channel, onStepChange, onChannelChange, onLogin }) => {
	const { currentProfileService, privacyService } = useServices();
	const askPrivacy = usePrivacyConfirmation();
	const isDesktop = useMediaQuery({ query: desktop });
	const stepCount = useSignupStepCount(channel);
	const visibleStep = Math.min(step, stepCount - 1);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [cgu, setCgu] = useState(false);
	const [fieldError, setFieldError] = useState("");
	const [indicator, setIndicator] = useState<CountryIndicator>("221");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [code, setCode] = useState("");
	const [confirmObj, setConfirmObj] = useState<ConfirmationResult>();
	const [seconds, setSeconds] = useState(0);
	const [phoneLoading, setPhoneLoading] = useState(false);

	useEffect(() => {
		if (step > stepCount - 1) {
			onStepChange(stepCount - 1);
		}
	}, [step, stepCount, onStepChange]);

	useEffect(() => {
		if (seconds <= 0) {
			return;
		}
		const timeout = window.setTimeout(() => setSeconds((current) => current - 1), 1000);
		return () => window.clearTimeout(timeout);
	}, [seconds]);

	const { run, loading, error } = useAsync(async () => {
		privacyService.acceptPrivacy();
		await currentProfileService.startEmailSignup(email.trim(), password);
	}, [email, password, privacyService]);

	const {
		run: runGoogle,
		loading: googleLoading,
		error: googleError,
	} = useAsync(async () => {
		const validated = await askPrivacy();
		if (!validated) {
			return;
		}
		await currentProfileService.signIn("Google");
	}, [askPrivacy]);

	const clearError = (): void => setFieldError("");

	const changeChannel = (next: SignupChannel): void => {
		clearError();
		onChannelChange(next);
	};

	const showChoice = visibleStep === 0;
	const showPassword = channel === "email" && (isDesktop ? showChoice : visibleStep === 1);
	const showOtp = channel === "phone" && visibleStep === 1;

	const sendSms = async (advance: boolean): Promise<void> => {
		const digits = phoneNumber.replace(/\D/g, "");
		if (digits.length < 6) {
			setFieldError(I18n.t("auth.signup.phoneInvalid"));
			return;
		}
		if (!cgu) {
			setFieldError(I18n.t("register.cgu.missing"));
			return;
		}
		setPhoneLoading(true);
		try {
			const setup = await currentProfileService.setupRecaptcha(`+${indicator}${digits}`);
			if (!setup) {
				setFieldError(I18n.t("auth.error.default"));
				return;
			}
			setConfirmObj(setup.confirm);
			setup.recaptcha.clear();
			setSeconds(30);
			if (advance) {
				onStepChange(1);
			}
		} catch (error) {
			setFieldError(messageForFirebaseError(error));
		} finally {
			setPhoneLoading(false);
		}
	};

	const verifyCode = async (): Promise<void> => {
		if (!confirmObj || code.trim().length !== 6) {
			setFieldError(I18n.t("auth.phoneVerify.invalid"));
			return;
		}
		setPhoneLoading(true);
		try {
			const result = await currentProfileService.checkUserWithPhoneNumber(confirmObj, code.trim());
			if (!result.user) {
				setFieldError(I18n.t("auth.phoneVerify.invalid"));
			}
		} catch {
			setFieldError(I18n.t("auth.phoneVerify.invalid"));
		} finally {
			setPhoneLoading(false);
		}
	};

	const credentialsReady = (): boolean => {
		if (channel === "email" && !isEmail(email.trim())) {
			setFieldError(I18n.t("form.invalid"));
			return false;
		}
		if (channel === "phone") {
			if (phoneNumber.replace(/\D/g, "").length < 6) {
				setFieldError(I18n.t("auth.signup.phoneInvalid"));
				return false;
			}
			if (!cgu) {
				setFieldError(I18n.t("register.cgu.missing"));
				return false;
			}
			return true;
		}
		if (!showPassword) {
			return true;
		}
		if (!isStrongPassword(password)) {
			setFieldError(I18n.t("auth.signup.passwordWeak"));
			return false;
		}
		if (password !== confirmPassword) {
			setFieldError(I18n.t("auth.signup.passwordMismatch"));
			return false;
		}
		if (!cgu) {
			setFieldError(I18n.t("register.cgu.missing"));
			return false;
		}
		return true;
	};

	const submitStep = async (): Promise<void> => {
		if (showOtp) {
			await verifyCode();
			return;
		}
		if (!credentialsReady()) {
			return;
		}
		if (channel === "phone") {
			await sendSms(true);
			return;
		}
		if (!showPassword) {
			onStepChange(1);
			return;
		}
		await run();
	};

	const fullPhone = `+${indicator} ${phoneNumber.replace(/\D/g, "")}`;
	const submitLabel = showOtp
		? I18n.t("auth.phoneVerify.submit")
		: showPassword && channel === "email"
			? I18n.t("auth.signup.submit")
			: I18n.t("auth.signup.continue");
	const shownError = error ?? googleError;

	return (
		<Form
			onSubmit={(event) => {
				event.preventDefault();
				void submitStep();
			}}
		>
			{showChoice && (
				<>
					<Title>{I18n.t("auth.signup.identity")}</Title>
					<Button
						variant="social"
						size="lg"
						block
						type="button"
						loading={googleLoading}
						onClick={() => void runGoogle()}
					>
						<img src={googleIcon} alt="" />
						{I18n.t("auth.signup.google")}
					</Button>
					<Or>
						<span>{I18n.t("auth.signup.or")}</span>
					</Or>
					<ChannelSwitch channel={channel} onChannelChange={changeChannel} />
					{channel === "email" ? (
						<Field>
							<Label htmlFor="signup-email">{I18n.t("email.placeholder")}</Label>
							<SimpleTextInput
								id="signup-email"
								value={email}
								onChange={(value) => {
									setEmail(value);
									clearError();
								}}
								placeholder={I18n.t("email.placeholder")}
								type="email"
								autoComplete="email"
								aria-describedby="signup-email-help"
							/>
							<Helper id="signup-email-help">{I18n.t("auth.signup.emailLead")}</Helper>
						</Field>
					) : (
						<Field>
							<Label htmlFor="signup-phone">{I18n.t("register.phonenumber.placeholder")}</Label>
							<PhoneRow>
								<IndicatorDataList value={indicator} onChange={setIndicator} />
								<SimpleTextInput
									id="signup-phone"
									value={phoneNumber}
									onChange={(value) => {
										setPhoneNumber(value);
										clearError();
									}}
									placeholder={I18n.t("register.phonenumber.placeholder")}
									type="tel"
									autoComplete="tel-national"
									aria-describedby="signup-phone-help"
								/>
							</PhoneRow>
							<Helper id="signup-phone-help">{I18n.t("auth.signup.phoneLead")}</Helper>
							<CguBox checked={cgu} onChange={setCgu} onClear={clearError} />
						</Field>
					)}
				</>
			)}

			{showPassword && (
				<>
					{!isDesktop && (
						<>
							<Title>{I18n.t("auth.signup.password")}</Title>
							<Lead>{I18n.t("auth.signup.passwordHelper")}</Lead>
						</>
					)}
					<Field>
						<Label htmlFor="signup-password">{I18n.t("password.placeholder")}</Label>
						<SimpleTextInput
							id="signup-password"
							value={password}
							onChange={(value) => {
								setPassword(value);
								clearError();
							}}
							placeholder={I18n.t("password.placeholder")}
							type="password"
							autoComplete="new-password"
							aria-describedby={isDesktop ? "signup-password-help" : undefined}
						/>
						{isDesktop && <Helper id="signup-password-help">{I18n.t("auth.signup.passwordHelper")}</Helper>}
					</Field>
					<Field>
						<Label htmlFor="signup-password-confirm">{I18n.t("auth.signup.passwordConfirm")}</Label>
						<SimpleTextInput
							id="signup-password-confirm"
							value={confirmPassword}
							onChange={(value) => {
								setConfirmPassword(value);
								clearError();
							}}
							placeholder={I18n.t("auth.signup.passwordConfirm")}
							type="password"
							autoComplete="new-password"
						/>
					</Field>
					<CguBox checked={cgu} onChange={setCgu} onClear={clearError} />
				</>
			)}

			{showOtp && (
				<>
					<Title>{I18n.t("auth.phoneVerify.title")}</Title>
					<Lead>{I18n.t("auth.phoneVerify.lead", { phone: fullPhone })}</Lead>
					<Field>
						<Label htmlFor="signup-otp">{I18n.t("register.phonenumber.otp.label")}</Label>
						<SimpleTextInput
							id="signup-otp"
							value={code}
							onChange={(value) => {
								setCode(value.replace(/\D/g, "").slice(0, 6));
								clearError();
							}}
							placeholder="000000"
							type="text"
							autoComplete="one-time-code"
						/>
					</Field>
					<Resend>
						{I18n.t("auth.phoneVerify.resendPrompt")}{" "}
						{seconds > 0 ? (
							<strong>{I18n.t("auth.phoneVerify.resendIn", { seconds })}</strong>
						) : (
							<SwitchLink type="button" onClick={() => void sendSms(false)} disabled={phoneLoading}>
								{I18n.t("auth.phoneVerify.resend")}
							</SwitchLink>
						)}
					</Resend>
				</>
			)}

			{channel === "phone" && <Recaptcha id="recaptcha-container" />}

			{fieldError && <ErrorContainer>{fieldError}</ErrorContainer>}
			{shownError && (
				<ErrorContainer>
					{shownError.message === RegisterError.EXISTS ? I18n.t("auth.error.exists") : I18n.t("auth.error.default")}
				</ErrorContainer>
			)}

			<Button variant="primary" size="lg" block loading={loading || phoneLoading || googleLoading} type="submit">
				{submitLabel}
			</Button>
			{!showOtp && (
				<Switch>
					{I18n.t("auth.signup.switchPrompt")}{" "}
					<SwitchLink type="button" onClick={onLogin}>
						{I18n.t("auth.signup.switchAction")}
					</SwitchLink>
				</Switch>
			)}
		</Form>
	);
};

const ChannelSwitch: React.FC<{ channel: SignupChannel; onChannelChange: (channel: SignupChannel) => void }> = ({
	channel,
	onChannelChange,
}) => (
	<Channels>
		<Channel type="button" $active={channel === "phone"} onClick={() => onChannelChange("phone")}>
			{I18n.t("auth.signup.phoneTab")}
		</Channel>
		<Channel type="button" $active={channel === "email"} onClick={() => onChannelChange("email")}>
			{I18n.t("auth.signup.emailTab")}
		</Channel>
	</Channels>
);

const CguBox: React.FC<{ checked: boolean; onChange: (value: boolean) => void; onClear: () => void }> = ({
	checked,
	onChange,
	onClear,
}) => {
	const { privacyService } = useServices();

	return (
		<Cgu>
			<input
				type="checkbox"
				checked={checked}
				onChange={(event) => {
					const accepted = event.currentTarget.checked;
					onChange(accepted);
					onClear();
					if (accepted) {
						privacyService.acceptPrivacy();
					}
				}}
			/>
			<span>
				{I18n.t("register.cgu.accept")}
				<Link to={Path.Cgu} target="_blank" rel="noreferrer">
					{I18n.t("register.cgu.link")}
				</Link>
			</span>
		</Cgu>
	);
};

function isStrongPassword(value: string): boolean {
	return value.length >= 8 && /[A-Za-z]/.test(value) && /\d/.test(value);
}

const Form = styled.form`
	${gap(16)};
	width: 100%;

	button[type="submit"] {
		margin-top: 8px;
	}
`;

const Title = styled.h1`
	margin: 0 0 6px;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	line-height: 1.2;
	color: #13131a;
`;

const Lead = styled.p`
	margin: 0 0 8px;
	color: #8a94a4;
	font-size: 15px;
	line-height: 1.5;
`;

const Helper = styled.span`
	color: #8a94a4;
	font-size: 13px;
	line-height: 1.45;
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
`;

const Label = styled.label`
	font-size: 13.5px;
	font-weight: 500;
	color: #5b6272;
`;

const Or = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	color: #8a94a4;
	font-size: 13px;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background: #e3e7ec;
	}
`;

const Channels = styled.div`
	display: flex;
	gap: 4px;
	padding: 4px;
	border-radius: 999px;
	background: #e7f4ec;
`;

const Channel = styled.button<{ $active: boolean }>`
	flex: 1;
	height: 40px;
	border: none;
	border-radius: 999px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	color: ${({ $active }) => ($active ? "#00863d" : "#5b6272")};
	background: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
	box-shadow: ${({ $active }) => ($active ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none")};
`;

const PhoneRow = styled.div`
	display: grid;
	grid-template-columns: 110px 1fr;
	gap: 8px;
	align-items: center;
`;

const Cgu = styled.label`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	cursor: pointer;
	font-size: 13.5px;
	line-height: 1.45;
	color: #5b6272;

	input {
		${checkboxInputCss};
		margin-top: 1px;
	}

	a {
		color: #00863d;
		font-weight: 600;
		text-decoration: underline;
	}
`;

const Resend = styled.p`
	margin: 0;
	text-align: center;
	font-size: 14px;
	color: #5b6272;

	strong {
		color: #00863d;
		font-weight: 600;
	}
`;

const Recaptcha = styled.div`
	position: absolute;
	width: 0;
	height: 0;
	overflow: hidden;
`;

const Switch = styled.p`
	margin: 22px 0 0;
	text-align: center;
	font-size: 14px;
	color: #5b6272;
`;

const SwitchLink = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	color: #00863d;
	font-size: 14px;
	font-weight: 600;

	&:disabled {
		cursor: default;
		opacity: 0.6;
	}
`;
